import React from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, MapPin, Clock } from 'lucide-react';
import { SecondaryModuleProps } from '../../types/secondaryModule';
import { SecondaryModuleContainer } from './SecondaryModuleContainer';
export function SafetyModule({
  moduleId,
  isOpen,
  onClose
}: SecondaryModuleProps) {
  // 模拟数据
  const data = {
    realtimeStatus: {
      todayTotal: 127,
      unprocessed: 37,
      urgentLevel: {
        high: 5,
        medium: 22,
        low: 100
      },
      hotspots: [{
        area: '东教学楼3层',
        count: 18,
        trend: 32
      }, {
        area: '操场西侧',
        count: 12,
        trend: -15
      }, {
        area: '食堂二楼',
        count: 9,
        trend: 8
      }],
      peakHours: [{
        hour: '12:00-13:00',
        count: 39
      }, {
        hour: '16:00-17:00',
        count: 28
      }, {
        hour: '08:00-09:00',
        count: 22
      }]
    },
    efficiency: {
      avgResponseTime: 8.5,
      completionRate: 89,
      falseAlarmRate: 12.3
    },
    aiInsights: [{
      type: 'finding' as const,
      content: '东教学楼3层走廊为本周高发区域，事件数量较上周增长32%'
    }, {
      type: 'finding' as const,
      content: '午休时段（12:00-13:00）事件集中度最高，占全天事件的31%'
    }, {
      type: 'suggestion' as const,
      content: '建议在东教学楼3层增加巡查频次，重点关注课间和午休时段'
    }, {
      type: 'suggestion' as const,
      content: '建议针对高年级学生开展防欺凌主题班会，强化同伴互助意识'
    }, {
      type: 'prediction' as const,
      content: '基于历史数据分析，下周该区域风险指数可能持续偏高'
    }]
  };
  return <SecondaryModuleContainer moduleId={moduleId} isOpen={isOpen} onClose={onClose} data-id="element-345">
      <div className="grid grid-cols-3 gap-6" data-id="element-346">
        {/* Left Column: Real-time Status */}
        <div className="col-span-2 space-y-6" data-id="element-347">
          {/* Today's Overview */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-348">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-349">
              <div className="w-1 h-5 bg-[#38BDF8] rounded-full" data-id="element-350"></div>
              实时守护态势
            </h3>
            <div className="grid grid-cols-4 gap-4" data-id="element-351">
              <div className="text-center" data-id="element-352">
                <div className="text-3xl font-bold text-white mb-1" data-id="element-353">
                  {data.realtimeStatus.todayTotal}
                </div>
                <div className="text-sm text-gray-400" data-id="element-354">今日报警总数</div>
              </div>
              <div className="text-center" data-id="element-355">
                <div className="text-3xl font-bold text-orange-400 mb-1" data-id="element-356">
                  {data.realtimeStatus.unprocessed}
                </div>
                <div className="text-sm text-gray-400" data-id="element-357">待处理</div>
              </div>
              <div className="text-center" data-id="element-358">
                <div className="text-3xl font-bold text-red-400 mb-1" data-id="element-359">
                  {data.realtimeStatus.urgentLevel.high}
                </div>
                <div className="text-sm text-gray-400" data-id="element-360">紧急</div>
              </div>
              <div className="text-center" data-id="element-361">
                <div className="text-3xl font-bold text-yellow-400 mb-1" data-id="element-362">
                  {data.realtimeStatus.urgentLevel.medium}
                </div>
                <div className="text-sm text-gray-400" data-id="element-363">重要</div>
              </div>
            </div>
          </div>

          {/* Hotspots */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-364">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-365">
              <MapPin className="w-5 h-5 text-[#38BDF8]" data-id="element-366" />
              高发区域分析
            </h3>
            <div className="space-y-3" data-id="element-367">
              {data.realtimeStatus.hotspots.map((spot, index) => <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg" data-id="element-368">
                  <div className="flex items-center gap-3" data-id="element-369">
                    <div className="w-8 h-8 rounded-full bg-[#38BDF8]/20 flex items-center justify-center text-[#38BDF8] font-bold" data-id="element-370">
                      {index + 1}
                    </div>
                    <span className="text-white" data-id="element-371">{spot.area}</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-372">
                    <span className="text-2xl font-bold text-white" data-id="element-373">
                      {spot.count}
                    </span>
                    <div className={`flex items-center gap-1 ${spot.trend > 0 ? 'text-red-400' : 'text-green-400'}`} data-id="element-374">
                      {spot.trend > 0 ? <TrendingUp className="w-4 h-4" data-id="element-375" /> : <TrendingDown className="w-4 h-4" data-id="element-376" />}
                      <span className="text-sm font-medium" data-id="element-377">
                        {Math.abs(spot.trend)}%
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Peak Hours */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-378">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-379">
              <Clock className="w-5 h-5 text-[#38BDF8]" data-id="element-380" />
              高发时段分布
            </h3>
            <div className="space-y-3" data-id="element-381">
              {data.realtimeStatus.peakHours.map((hour, index) => <div key={index} className="flex items-center gap-4" data-id="element-382">
                  <div className="w-32 text-gray-400 text-sm" data-id="element-383">{hour.hour}</div>
                  <div className="flex-1 h-8 bg-white/5 rounded-full overflow-hidden" data-id="element-384">
                    <div className="h-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] rounded-full flex items-center justify-end pr-3" style={{
                  width: `${hour.count / 39 * 100}%`
                }} data-id="element-385">
                      <span className="text-white text-sm font-bold" data-id="element-386">
                        {hour.count}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Efficiency Metrics */}
          <div className="glass-panel p-6 rounded-xl border border-white/10" data-id="element-387">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-388">
              <div className="w-1 h-5 bg-[#38BDF8] rounded-full" data-id="element-389"></div>
              守护效能分析
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-390">
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-391">
                <div className="text-3xl font-bold text-[#38BDF8] mb-1" data-id="element-392">
                  {data.efficiency.avgResponseTime}min
                </div>
                <div className="text-sm text-gray-400" data-id="element-393">平均响应时间</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-394">
                <div className="text-3xl font-bold text-green-400 mb-1" data-id="element-395">
                  {data.efficiency.completionRate}%
                </div>
                <div className="text-sm text-gray-400" data-id="element-396">处理完成率</div>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg" data-id="element-397">
                <div className="text-3xl font-bold text-yellow-400 mb-1" data-id="element-398">
                  {data.efficiency.falseAlarmRate}%
                </div>
                <div className="text-sm text-gray-400" data-id="element-399">误报率</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: AI Insights */}
        <div className="space-y-6" data-id="element-400">
          <div className="glass-panel p-6 rounded-xl border border-[#0EA5E9]/30 bg-gradient-to-br from-[#0EA5E9]/10 to-transparent" data-id="element-401">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-402">
              <div className="w-8 h-8 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center" data-id="element-403">
                <AlertTriangle className="w-5 h-5 text-[#0EA5E9]" data-id="element-404" />
              </div>
              易教智能体洞察
            </h3>
            <p className="text-sm text-gray-400 mb-6" data-id="element-405">
              基于多维数据分析，易教智能体为您生成以下洞察：
            </p>

            <div className="space-y-4" data-id="element-406">
              {/* Key Findings */}
              <div data-id="element-407">
                <div className="text-sm font-bold text-[#38BDF8] mb-2 flex items-center gap-2" data-id="element-408">
                  💡 关键发现
                </div>
                <div className="space-y-2" data-id="element-409">
                  {data.aiInsights.filter(insight => insight.type === 'finding').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-[#38BDF8]/30 py-1" data-id="element-410">
                        {insight.content}
                      </div>)}
                </div>
              </div>

              {/* Suggestions */}
              <div data-id="element-411">
                <div className="text-sm font-bold text-green-400 mb-2 flex items-center gap-2" data-id="element-412">
                  🎯 建议措施
                </div>
                <div className="space-y-2" data-id="element-413">
                  {data.aiInsights.filter(insight => insight.type === 'suggestion').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-green-400/30 py-1" data-id="element-414">
                        {insight.content}
                      </div>)}
                </div>
              </div>

              {/* Predictions */}
              <div data-id="element-415">
                <div className="text-sm font-bold text-orange-400 mb-2 flex items-center gap-2" data-id="element-416">
                  📊 预测趋势
                </div>
                <div className="space-y-2" data-id="element-417">
                  {data.aiInsights.filter(insight => insight.type === 'prediction').map((insight, index) => <div key={index} className="text-sm text-gray-300 pl-4 border-l-2 border-orange-400/30 py-1" data-id="element-418">
                        {insight.content}
                      </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SecondaryModuleContainer>;
}